import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { SplitText, Magnetic } from './effects';

const SLIDES = [
  {
    label: 'Ustalık · Doğanın Sessizliğinde',
    title1: 'Tabağa giden yol,',
    title2: 'doğada başlar.',
    subtitle:
      'Lezzet bir tesadüf değildir. Hammadeden tabağa kadar her aşamayı disiplinle yöneten ustalarla, doğanın ritmiyle çalışırız.',
    image: '/hero/slide-01.webp',
    // Mobile crop: chef sits in the right-third of the wide image,
    // so we anchor the focal area near the right to keep him centered on phones.
    objectClass: 'object-[78%_center] lg:object-center',
    secondary: { label: 'Sistemi İncele', href: '/tasima-yemek' },
  },
  {
    label: 'Saatinde, sıcaklığında',
    title1: 'Üretimden servise,',
    title2: 'tek elden operasyon.',
    subtitle:
      'Merkezi mutfak — izotermli filo — saha servisi. Vardiyanıza dakika hassasiyetiyle, sertifikalı bir disiplinle teslim ediyoruz.',
    image: '/hero/slide-02.webp',
    objectClass: 'object-[68%_center] lg:object-center',
    secondary: { label: 'Sistemi Gör', href: '/tasima-yemek' },
  },
  {
    label: 'Ölçek · Tutarlılık · Disiplin',
    title1: 'Büyük ölçekli',
    title2: 'kurumsal disiplin.',
    subtitle:
      'Günlük binlerce öğün — aynı saat, aynı kalite, aynı standart. Sertifikalı ekip, tutarlı operasyon.',
    image: '/hero/slide-03.webp',
    objectClass: 'object-center',
    secondary: { label: 'Kapasitemiz', href: '/hakkimizda' },
  },
];

const SLIDE_DURATION = 8500;

/* Dark cinematic overlay — gives crisp contrast for cream + gold text on top of any image. */
const OVERLAY = `
  linear-gradient(180deg,
    rgba(10,12,11,0.55) 0%,
    rgba(10,12,11,0.30) 25%,
    rgba(10,12,11,0.10) 45%,
    rgba(10,12,11,0.45) 65%,
    rgba(10,12,11,0.85) 92%,
    rgba(10,12,11,0.95) 100%),
  linear-gradient(90deg,
    rgba(10,12,11,0.55) 0%,
    rgba(10,12,11,0.10) 55%,
    rgba(10,12,11,0) 100%)
`;

const Hero = () => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setI((p) => (p + 1) % SLIDES.length), SLIDE_DURATION);
    return () => clearInterval(t);
  }, [paused]);

  const slide = SLIDES[i];

  return (
    <section
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="relative h-screen min-h-[680px] w-full overflow-hidden bg-[#0A0C0B]"
    >
      {SLIDES.map((s, idx) => (
        <div
          key={idx}
          aria-hidden={idx !== i}
          className={`absolute inset-0 transition-opacity duration-[1400ms] ease-out ${
            idx === i ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={s.image}
            alt=""
            className={`w-full h-full object-cover ${s.objectClass} ${
              idx === i ? 'animate-[heroZoom_9s_ease-out_forwards]' : ''
            }`}
          />
          <div className="absolute inset-0" style={{ background: OVERLAY }} />
        </div>
      ))}

      <div className="relative z-10 h-full flex items-end pb-24 lg:pb-32">
        <div className="container-x w-full">
          <div className="max-w-3xl">
            <div
              key={`label-${i}`}
              className="flex items-center gap-4 mb-7"
              style={{ animation: 'fadeInLabel 700ms ease-out 100ms backwards' }}
            >
              <span className="text-[11px] numeric tracking-[0.32em] text-[#C9A875] font-medium">
                0{i + 1} / 03
              </span>
              <span className="h-px w-10 bg-[#C9A875]/65" />
              <span
                className="text-[10.5px] tracking-[0.32em] uppercase font-medium"
                style={{ color: 'rgba(245,240,229,0.85)' }}
              >
                {slide.label}
              </span>
            </div>

            <h1
              key={`title-${i}`}
              className="font-display text-[42px] sm:text-[60px] lg:text-[86px] leading-[0.98] tracking-[-0.02em] text-[#F5F0E5]"
              style={{
                fontWeight: 500,
                textShadow: '0 2px 24px rgba(0,0,0,0.35)',
              }}
            >
              <SplitText text={slide.title1} delay={120} />
              <br />
              <span className="italic font-light text-[#C9A875]">
                <SplitText text={slide.title2} delay={400} />
              </span>
            </h1>

            <p
              key={`sub-${i}`}
              className="mt-7 lg:mt-8 text-[16px] lg:text-[19px] max-w-xl leading-[1.6]"
              style={{
                color: 'rgba(245,240,229,0.88)',
                animation: 'fadeUpSoft 900ms cubic-bezier(0.22,1,0.36,1) 700ms backwards',
                textShadow: '0 1px 16px rgba(0,0,0,0.30)',
              }}
            >
              {slide.subtitle}
            </p>

            <div
              key={`cta-${i}`}
              className="mt-10 lg:mt-12 flex flex-wrap items-center gap-7"
              style={{ animation: 'fadeUpSoft 900ms cubic-bezier(0.22,1,0.36,1) 950ms backwards' }}
            >
              <Magnetic strength={0.2}>
                <Link
                  to="/iletisim"
                  className="group inline-flex items-center gap-3 bg-[#C9A875] text-[#0A0C0B] px-8 py-[18px] text-[12px] tracking-[0.22em] uppercase font-semibold rounded-sm transition-colors duration-300 hover:bg-[#E0C896]"
                  data-testid="hero-primary-cta"
                >
                  Teklif Al
                  <ArrowRight
                    size={14}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </Link>
              </Magnetic>
              <Link
                to={slide.secondary.href}
                className="group inline-flex items-center gap-2 text-[12.5px] tracking-[0.18em] uppercase font-medium border-b pb-1.5 transition-colors duration-300"
                style={{
                  color: '#F5F0E5',
                  borderColor: 'rgba(245,240,229,0.45)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#C9A875';
                  e.currentTarget.style.borderColor = '#C9A875';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#F5F0E5';
                  e.currentTarget.style.borderColor = 'rgba(245,240,229,0.45)';
                }}
              >
                {slide.secondary.label}
                <ArrowRight
                  size={13}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM controls */}
      <div className="absolute bottom-8 inset-x-0 z-10">
        <div className="container-x flex items-end justify-between gap-6">
          <div className="flex items-end gap-6">
            {SLIDES.map((s, idx) => (
              <button
                key={idx}
                onClick={() => setI(idx)}
                aria-label={s.label}
                className="group flex flex-col items-start gap-2.5 text-left"
              >
                <span
                  className={`text-[11px] numeric tracking-[0.3em] transition-colors ${
                    idx === i ? 'text-[#F5F0E5]' : 'text-[#F5F0E5]/40 group-hover:text-[#F5F0E5]/70'
                  }`}
                >
                  0{idx + 1}
                </span>
                <span
                  className={`block h-[2px] transition-all duration-500 ${
                    idx === i
                      ? 'w-16 bg-[#C9A875]'
                      : 'w-8 bg-[#F5F0E5]/20 group-hover:bg-[#F5F0E5]/40'
                  }`}
                />
              </button>
            ))}
          </div>

          <a
            href="#stats"
            aria-label="Aşağı kaydır"
            className="hidden md:inline-flex items-center justify-center h-9 w-9 text-[#C9A875] hover:text-[#F5F0E5] transition-colors"
          >
            <ChevronDown size={18} className="animate-bounce" strokeWidth={1.6} />
          </a>
        </div>
      </div>

      <style>{`
        @keyframes heroZoom {
          from { transform: scale(1.02); }
          to { transform: scale(1.10); }
        }
        @keyframes fadeUpSoft {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInLabel {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </section>
  );
};

export default Hero;
